import React from "react";
import dec from "ramda/src/dec";

import { useNetwork } from "../../hooks/useNetwork";
import { STICKY_HEADER_STYLES } from "../../common/styles";
import { DownloadCSVButton } from "../../components/DownloadCSVButton";
import { L2Selector, L3Selector } from "../../common/network/NetworkSelectors";
import { usePageTablePaginationContext } from "../../components/PageTablePagination/context";
import { SelectWrapper } from "../../components/SelectWrapper";
import { ReportCheckboxes } from "../../components/ReportCheckboxes";
import { UserReportTable } from "../../components/UserReportTable";
import { COLOR_BLUE_BAYOUX } from "../../constants/styles";
import { MainLayout } from "../../layout/MainLayout";
import useAuth from "../login/AuthContext";
import Api from "../../services/Api";
import { Grid } from "../../UI/Grid";
import { Select } from "../../UI/Select";
import { Typography } from "../../UI/Typography";

const initialState: Record<string, any> = {
  subnetwork: 0,
  unitType: 0,
  specialReport: null,
  userStatus: {
    title: "Select User Status",
    selection: null,
  },
  registeredStatus: {
    title: "Selected Registration Status",
    selection: null,
  },
  shouldLoad: true,
  results: { results: [], totalSize: 0 },
  checkboxes: {
    includeQuickJoinInfo: false,
    includeRegistrationInfo: false,
    includeSocialAccountsInfo: false,
    includeRoommatesInfo: false,
    includeHousingInfo: false,
    includeMajorsInfo: false,
    includeLifestyleQuestionInfo: false
  },
  askDownloadAll: false,
};

const optionsForUserStatus = [
  { name: "All Users", id: "all_users" },
  { name: "Unmatched", id: "unmatched" },
  { name: "Partially Filled Room", id: "partially_filled" },
  { name: "Completely Filled Room", id: "completely_filled" },
];

const USER_REPORT_INFO_TEXT = "Select from the drop-downs below to generate a User Report. You can also download the CSV version for additional data on each user including: invitation link, registration status, email, import date, registration date, academic majors, gender/roommate matching preference, lifestyle question responses, social account handles and roommates.";

export const UserReportPage = () => {
  const { getAuthToken, isSuperAdminRead } = useAuth();
  const { selectedNetwork, getL3Options } = useNetwork();

  const [state, setState] = React.useState(initialState);
  const { page, setPage, setTotal } = usePageTablePaginationContext();
  const [loading, setLoading] = React.useState(false);

  const handleUserStatusSelection = (selection: any) => {
    setState({
      ...state,
      userStatus: {
        title: optionsForUserStatus.filter(
          (x) => `${x.id}` === selection.value
        )[0].name,
        selection,
      },
      shouldLoad: true,
      roomSize: null,
      askDownloadAll: state.subnetwork == 0,
    });
    setPage(1);
  };

  const handleL2Selection = (selection: any) => {
    let newState;
    if (selection === "all_matching_rounds" || selection == "recent_matching_rounds") {
      newState = {
        ...state,
        specialReport: selection,
        subnetwork: 0,
        unitType: selection === 0 ? 0 : null,
        shouldLoad: true,
        askDownloadAll: false,
        results: { results: [], totalSize: 0 },
      };
    } else {
      newState = {
        ...state,
        subnetwork: selection,
        unitType: selection === 0 ? 0 : null,
        shouldLoad: true,
        askDownloadAll: false,
        results: { results: [], totalSize: 0 },
      };
    }
    setState(newState);
    setPage(1);
  };

  const handleL3Selection = (selection: any) => {
    setState({
      ...state,
      unitType: selection,
      shouldLoad: true,
      askDownloadAll: false,
      userStatus: selection === 0 ? {
        title: "Select User Status",
        selection: null,
      } : state.userStatus,
    });
    setPage(1);
  };

  React.useEffect(() => {
    setState(initialState);
    setPage(1);
  }, [selectedNetwork.l1Id]);

  React.useEffect(() => {
    setState({ ...state, shouldLoad: true });
  }, [page]);

  const handleCheckboxSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({
   ...state,
   checkboxes: {
    ...state.checkboxes,
    [e.target.name]: e.target.checked,
   }
      
    });
  };

  React.useEffect(() => {
    if (
      // state.subnetwork === 0 &&
      state.unitType === 0 &&
      state.shouldLoad
    ) {
      setState({
        ...state,
        shouldLoad: false,
        askDownloadAll: false,
        results: { results: [], totalSize: 0 },
        roomSize: null
      });
      setTotal(0);

      return;
    }
    if (
      state.subnetwork &&
      state.unitType === 0 &&
      state.userStatus.selection &&
      state.shouldLoad
    ) {
      setState({ ...state, shouldLoad: false, askDownloadAll: true });
      return;
    }
    if (
      state.subnetwork &&
      state.unitType &&
      state.userStatus.selection &&
      state.shouldLoad
    ) {
      const loadReport = async () => {
        const results = await Api.getUserReport(
          selectedNetwork.l1Id,
          state.subnetwork,
          state.unitType,
          state.userStatus.selection.value,
          state.registeredStatus.selection,
          dec(page),
          await getAuthToken()
        );

        if (results.error) {
        } else {
          const roomSize = getL3Options(
            selectedNetwork.l1Id,
            state.subnetwork
          ).filter((x) => x.l3Id === state.unitType)[0].size;

          setState({
            ...state,
            shouldLoad: false,
            results: results.response,
            roomSize,
          });
          setTotal(results.response.totalResults);
        }
        setLoading(false);
      };

      loadReport();
      setLoading(true);
    }

    if (state.shouldLoad) {
      setState({ ...state, shouldLoad: false });
    }
  }, [state.shouldLoad, page]);

  const downloadUserReportCsvCallback = async () =>
    Api.getUserReportCSV(
      selectedNetwork.l1Id,
      state.subnetwork,
      state.unitType,
      state.checkboxes,
      state.userStatus.selection.value,
      await getAuthToken(),
    );

  const isDownloadDisabled =
    !state.askDownloadAll &&
    (!selectedNetwork.l1Id ||
      !state.subnetwork ||
      state.unitType === null ||
      state.unitType === undefined ||
      !state.userStatus.selection);

  const reloadResults = () => {
    setState((prevState) => ({ ...prevState, shouldLoad: true }));
  };

  return (
    <MainLayout
      title="User Report"
      stickyHeader={true}
    >
      <Grid container spacing={2} sx={STICKY_HEADER_STYLES.withTextAtTheTop}>
        <Grid item xs={12}>
          <Typography>
            {USER_REPORT_INFO_TEXT}
          </Typography>
        </Grid>
        <Grid container xs={12} pl={1.3} spacing={2}>
          <ReportCheckboxes handleCheckboxSelection={handleCheckboxSelection} checkboxes={state.checkboxes} />
        </Grid>
        <Grid item container spacing={1} mb={3}>
          <Grid item>
            <L2Selector
              onSelectHandler={handleL2Selection}
            />
          </Grid>
          {state.subnetwork !== 0 && (
            <Grid item>
              <L3Selector
                onSelectHandler={handleL3Selection}
                extraMenuOptions={[{ value: 0, label: "All Unit Types" }] as any}
                l2Selection={state.subnetwork}
                fullWidth
              />
            </Grid>
          )}
          {state.unitType !== null && state.subnetwork !== 0 && (
            <Grid item>
              <SelectWrapper>
                <Select
                  label="Match Status"
                  id="user-status-select"
                  value={state.userStatus.selection}
                  onChange={handleUserStatusSelection}
                  options={optionsForUserStatus.map((option) => ({
                    value: option.id,
                    label: option.name,
                  }))}
                />
              </SelectWrapper>
            </Grid>
          )}
        </Grid>
        <Grid item xs={12}>
          <DownloadCSVButton
            isDisabled={isDownloadDisabled || isSuperAdminRead()}
            apiCall={downloadUserReportCsvCallback}
          >
            Download CSV Report
          </DownloadCSVButton>
        </Grid>
        {!state.results.results.length && !!state.shouldLoad && (
          <Grid item xs={12}>
            <Typography variant="body2" color={COLOR_BLUE_BAYOUX}>
              No users found
            </Typography>
          </Grid>
        )}
        {!!state.results.results.length && (
          <Grid item xs={12}>
            <UserReportTable
              isLoading={loading}
              reloadResults={reloadResults}
              results={state.results.results}
              roommatesQty={state.roomSize}
            />
          </Grid>
        )}
      </Grid>
    </MainLayout>
  );
};
